import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StatusPillVariant } from './status-pill.util';

// width: 100% is load-bearing, not decorative, and now applies on every viewport: the parent list
// (upcoming-bookings Container) is a single-column flex-direction: column + align-items: center
// layout on every viewport (no more desktop flex-wrap multi-column grid). Cross-axis sizing for a
// non-stretched flex item (align-items: center, rather than the flex default, stretch) falls back
// to CSS fit-content, and the Title's nowrap text (or any other unbreakable run) contributes its
// full, un-ellipsized width to that calculation's content-based minimum, which can exceed the
// viewport. align-items: center then centers the oversized box and clips the excess symmetrically
// off both edges — clipping exactly the 1px left/right border while leaving the top/bottom border
// (which spans the whole width, on- and off-screen alike) visible. box-sizing: border-box (already
// global, see root.styles.ts) does NOT fix this on its own, since there's no explicit width for it
// to reinterpret — an explicit width removes the ambiguity.
export const RedesignedBookingTileContainer = styled.div<{ $isSelected?: boolean }>`
  background-color: ${({ theme }) => theme.colors.palette.sharedWhite};
  border: 1px solid
    ${({ $isSelected, theme }) => ($isSelected ? theme.colors.primary : theme.colors.palette.systemGreyMedium)};
  border-radius: 12px;
  /*
   * The selected tile reads as a thicker brand border, drawn as a RING rather than a wider border:
   * growing the border would move this tile's content by a pixel and nudge every tile below it
   * each time the reader picks a different booking.
   */
  box-shadow: ${({ $isSelected, theme }) => ($isSelected ? `0 0 0 1px ${theme.colors.primary}, ` : '')}0 1px 3px 0
      rgba(10, 13, 18, 0.08),
    0 1px 2px 0 rgba(10, 13, 18, 0.04);
  overflow: hidden;
  width: 100%;
  // Load-bearing, not decorative (like the width: 100% note above): this is a direct flex item of
  // upcoming-bookings/components/styles.ts's Container (flex-direction: column, max-height +
  // overflow: scroll — it's meant to scroll past the viewport, not fit everything into it). Flex
  // items default to flex-shrink: 1, so once enough tiles are rendered that their combined natural
  // height exceeds Container's max-height, the flex algorithm proportionally SHRINKS every tile to
  // force them all into the available space instead of letting Container's overflow: scroll take
  // over -- and since this box has overflow: hidden with no explicit height, the shrunk box clips
  // away all of its content (title/date/status/footer), leaving only this border + background
  // visible as a hairline. Only reproduces once a user has enough bookings for the list to actually
  // overflow the viewport (confirmed via a real account with 25 upcoming bookings; a handful of
  // bookings never triggers it, which is why this went unnoticed). flex-shrink: 0 forces each card
  // to keep its natural height so Container's own overflow: scroll handles the overflow instead.
  flex-shrink: 0;
`;

// RES-1174: `$isInert` is set for a rejected booking, which is not a click target. The pointer
// cursor and the hover tint have to go with the role and the handler -- an element that lights up
// under the cursor and then does nothing reads as broken rather than as inert.
export const Header = styled.div<{ $isInert?: boolean }>`
  padding: 20px 20px 16px;
  cursor: ${({ $isInert }) => ($isInert ? 'default' : 'pointer')};
  transition: background-color 0.15s ease;

  // Desktop-only affordance (hover has no touch equivalent, so this is a no-op on mobile): mirrors
  // service-request-tile's Container:hover rule (service-requests/src/components/service-request-tile/styles.ts),
  // which gives its whole clickable card a visible hover state. Here only the header (title/date) is
  // the click target -- see redesigned-booking-tile.tsx's comment on why the footer's buttons stay
  // outside it -- so the hover tint is scoped to this element rather than the outer
  // RedesignedBookingTileContainer.
  ${({ $isInert, theme }) =>
    !$isInert &&
    css`
      &:hover {
        background-color: ${theme.colors.palette.systemGreyLight};
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/* `flex-start`, so the pill sits on the resource name rather than between it and the building. */
export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

/*
 * RES-1367: the resource and the building it is in, stacked.
 *
 * The building used to be appended to the TIME -- "9:00 - 10:00 AM / One Beacon Street" -- which
 * put the room's address in the sentence about when it was booked, and left the title row with one
 * line where the design has two. It is a property of the room, so it reads under the room's name.
 */
export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const truncatedLine = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Title = styled.div`
  font: 600 16px/1.3 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
  ${truncatedLine}
`;

export const Subtitle = styled.div`
  margin-top: 2px;
  font: 400 12px/1.3 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  ${truncatedLine}
`;

export const StatusCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const StatusPillContainer = styled.div<{ variant: StatusPillVariant }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  background-color: ${({ theme, variant }) => {
    if (variant === 'positive') return theme.colors.palette.systemGreenLight;
    if (variant === 'pending') return theme.colors.palette.systemOrangeLight;
    return theme.colors.palette.systemPinkLight;
  }};
`;

export const StatusDot = styled.span<{ variant: StatusPillVariant }>`
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background-color: ${({ theme, variant }) => {
    if (variant === 'positive') return theme.colors.palette.systemGreen;
    if (variant === 'pending') return theme.colors.palette.systemOrange;
    return theme.colors.palette.systemPink;
  }};
`;

export const StatusLabel = styled.span<{ variant: StatusPillVariant }>`
  font: 500 13px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme, variant }) => {
    if (variant === 'positive') return theme.colors.palette.systemGreen;
    if (variant === 'pending') return theme.colors.palette.systemOrange;
    return theme.colors.palette.systemPink;
  }};
`;

/* One child since the calendar icon went, so no gap to set between anything. */
export const DateRow = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
`;

/**
 * RES-1174. The landlord's explanation, under the date on a rejected tile.
 *
 * Defined here rather than shared with the legacy tile's copy in ../styles.ts, even though the
 * two currently resolve to the same grey: this folder exists to keep the redesign isolated behind
 * its flag, and a shared styled component would let a restyle of one tile silently move the other.
 *
 * Clamped to three lines rather than one. The reason is capped at 280 characters server-side and
 * it is the whole point of the tile, so a one-line clamp would hide most of one that used its
 * allowance. React escapes it -- it is text written by one user and shown to another.
 */
export const RejectionReason = styled.div`
  font: 400 14px/1.35 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  padding-top: 8px;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DateText = styled.div`
  font: 400 14px/1.35 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const BoldSpan = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.palette.systemGreyMedium};
`;

export const CheckedInRow = styled.div`
  padding: 14px 20px;
`;

/*
 * RES-1367: `text-align: right`, so cancel sits at the SAME edge whether or not check-in is beside
 * it. With both, `FooterButtonsRow` puts check-in left and cancel right; alone, this row had it on
 * the left -- so the control jumped sides from one tile to the next down a list where some bookings
 * are checkable-in and some are not.
 */
export const FooterCancelRow = styled.button`
  display: block;
  width: 100%;
  padding: 14px 20px;
  border: none;
  background: none;
  text-align: right;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const CancelText = styled.span<{ disabled?: boolean }>`
  font: 500 15px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemPink};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

// Same weight/size as CancelText, just in the app's primary color rather than pink.
export const CheckInText = styled.span`
  font: 500 15px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.primary};
`;

// Same padding as FooterCancelRow's single row; the two actions sit at opposite ends of the row
// (Check in at the left edge, Cancel booking at the right edge) via space-between rather than each
// stretching to fill/center in its own half.
export const FooterButtonsRow = styled.div`
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// Same plain, borderless text-link treatment as FooterCancelRow (the default single-row cancel) —
// sized to its text rather than stretched, so it sits compact next to its sibling instead of
// spreading across half the row. Shared by both the check-in and cancel actions here; only the
// nested text color differs (CheckInText vs CancelText). No fixed height: FooterCancelRow doesn't
// set one either (its row height comes purely from FooterButtonsRow's padding around the text's
// natural line-height) — a fixed height here was a leftover from the old filled-button treatment
// and was inflating this row taller than the single-button row above/below it.
export const InlineFooterButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

// RES-1076: replaces the footer's normal cancel row/buttons once cancellation is confirmed inline
// (see redesigned-booking-tile.tsx's useCancelConfirmation) -- same "expand in place, no modal"
// pattern as pages/booking-details/booking-details.tsx, just sized to the tile's compact footer
// rather than the details page's full-width card.
export const CancelConfirmSection = styled.div`
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const CancelConfirmText = styled.div`
  font: 400 13px/1.4 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CancelConfirmButtonsRow = styled.div`
  display: flex;
  gap: 10px;
`;

const confirmButtonBaseStyles = css`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const KeepBookingButton = styled.button`
  ${confirmButtonBaseStyles}
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  background-color: ${({ theme }) => theme.colors.palette.sharedWhite};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const ConfirmCancelButton = styled.button`
  ${confirmButtonBaseStyles}
  border: none;
  background-color: ${({ theme }) => theme.colors.palette.systemPink};
  color: ${({ theme }) => theme.colors.palette.sharedWhite};
`;

export const MessageIconContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const MessageIcon = styled(FontAwesomeIcon)`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const UnreadBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9999px;
  background-color: ${({ theme }) => theme.colors.palette.systemPink};
  color: ${({ theme }) => theme.colors.palette.sharedWhite};
  font: 600 11px/18px ${({ theme }) => theme.fonts.join()};
  display: flex;
  align-items: center;
  justify-content: center;
`;
